import React, { useCallback, useState } from 'react';
import { FiClock, FiEdit2, FiPause, FiPlay, FiX } from 'react-icons/fi';
import { useI18n } from '../utilities/i18n';
import {
  canPauseClock,
  canResumeClock,
  clockSeconds,
  formatClock,
  onlineStatusLabelKey,
  pauseClock,
  resumeClock,
  setClockSeconds,
  splitSeconds,
  toggleClock,
  useClockHotkey,
  useOnlineClock,
} from '../utilities/onlineMatchStore';
import '../styles/gamesManagement.css';

/**
 * Таймер онлайн-матча: пауза, запуск, правка минут и секунд, пробел — пауза/продолжить.
 * variant="bar" — полная панель (карточка событий), "controls" — только кнопки (пульт табло).
 * baseClock — с чего начать, если в этом браузере таймера ещё нет (матч запущен с другого устройства).
 */
export default function OnlineClockBar({
  matchId, onOpenOnline, variant = 'bar', baseClock = null, hotkey = true,
}) {
  const { t } = useI18n();
  const { clock: stored, seconds: storedSeconds, setClock } = useOnlineClock(matchId);
  const clock = stored || baseClock;
  const seconds = stored ? storedSeconds : Math.floor(clockSeconds(baseClock));
  const [edit, setEdit] = useState(null);

  const apply = useCallback((fn) => setClock((current) => fn(current || baseClock)), [setClock, baseClock]);
  const toggle = useCallback(() => apply(toggleClock), [apply]);
  useClockHotkey(Boolean(hotkey && clock && !edit), toggle);

  if (!clock) return null;
  const canPause = canPauseClock(clock);
  const canResume = canResumeClock(clock);
  const compact = variant === 'controls';

  const startEdit = () => {
    const { minute, second } = splitSeconds(seconds);
    setEdit({ minute: String(minute), second: String(second).padStart(2, '0') });
  };

  const applyEdit = () => {
    if (!edit) return;
    const minute = Math.max(0, Number(edit.minute) || 0);
    const second = Math.min(59, Math.max(0, Number(edit.second) || 0));
    apply((current) => setClockSeconds(current, minute * 60 + second));
    setEdit(null);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') applyEdit();
    if (e.key === 'Escape') setEdit(null);
  };

  return (
    <div className={`gm-online-clock${canPause ? ' is-running' : ''}${compact ? ' is-compact' : ''}`}>
      {compact ? null : <FiClock className="gm-online-clock-icon" aria-hidden="true" />}
      {compact ? null : <span className="gm-online-clock-status">{t(onlineStatusLabelKey(clock.status))}</span>}
      {edit ? (
        <div className="gm-online-clock-edit">
          <div className="gm-time-input">
            <input
              className="st-input gm-time-part"
              inputMode="numeric"
              maxLength={3}
              aria-label={t('gmMinute')}
              autoFocus
              value={edit.minute}
              onChange={(e) => setEdit((prev) => ({ ...prev, minute: e.target.value.replace(/[^0-9]/g, '').slice(0, 3) }))}
              onKeyDown={onKeyDown}
            />
            <span className="gm-time-sep">:</span>
            <input
              className="st-input gm-time-part"
              inputMode="numeric"
              maxLength={2}
              aria-label={t('gmSecond')}
              value={edit.second}
              onChange={(e) => {
                const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                setEdit((prev) => ({ ...prev, second: digits === '' ? '' : (Number(digits) > 59 ? '59' : digits) }));
              }}
              onKeyDown={onKeyDown}
            />
          </div>
          <button type="button" className="st-btn st-btn-primary gm-mini-btn" onClick={applyEdit}>OK</button>
          <button type="button" className="st-btn st-btn-ghost gm-mini-btn" onClick={() => setEdit(null)} aria-label={t('cancel')}><FiX /></button>
        </div>
      ) : (
        <>
          {compact ? null : (
            <button type="button" className="gm-online-clock-time" title={t('olmEditTimer')} onClick={startEdit}>
              {formatClock(seconds)}
            </button>
          )}
          <div className="gm-online-clock-actions">
            {canPause ? (
              <button type="button" className="st-btn st-btn-secondary gm-mini-btn" title={t('olmSpaceHint')} onClick={() => apply(pauseClock)}>
                <FiPause /> {t('olmPauseShort')}
              </button>
            ) : null}
            {canResume ? (
              <button type="button" className="st-btn st-btn-primary gm-mini-btn" title={t('olmSpaceHint')} onClick={() => apply(resumeClock)}>
                <FiPlay /> {t('olmResumeShort')}
              </button>
            ) : null}
            <button type="button" className="st-btn st-btn-ghost gm-mini-btn" onClick={startEdit} title={t('olmEditTimer')}>
              <FiEdit2 /> {t('olmEditTimerShort')}
            </button>
            {/* Перерыв: следующий тайм запускается в окне онлайна / кнопками этапов (там же уходит смена статуса на сервер). */}
            {canPause || canResume || !onOpenOnline ? null : (
              <button type="button" className="st-btn st-btn-secondary gm-mini-btn" onClick={onOpenOnline}>
                {t('mrOnlineMatch')}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Кнопка «подставить время с таймера» в форме события. */
export function OnlineClockNowButton({ matchId, onPick }) {
  const { t } = useI18n();
  const { clock, seconds } = useOnlineClock(matchId);
  return (
    <button
      type="button"
      className="st-btn st-btn-ghost gm-mini-btn gm-time-now"
      title={t('olmUseTimerTime')}
      onClick={() => onPick(seconds, clock)}
    >
      <FiClock /> {formatClock(seconds)}
    </button>
  );
}
